import dayjs from 'dayjs';
import {
  Guest,
  GUEST_FORM_NAMES,
  Location,
  PhoneDetails,
  SelectOption,
} from '@chekin/core';
import {
  isAgreementClauseField,
  isDateField,
  isPhoneField,
  isSelectField,
  isSelectOptionTypeValid,
  buildAgreementClausesFormData,
} from './utils';

const getIsEmptyPhoneValue = (value: unknown) => {
  return Object.values(value as PhoneDetails).some(val => !val);
};

const checkIsLocationValue = (value: unknown): value is Location => {
  return Boolean((value as Location)?.code && (value as Location)?.name);
};

const CitiesDTO: Record<string, string> = {
  [GUEST_FORM_NAMES.birth_place_municipality]: GUEST_FORM_NAMES.birth_place_city,
  [GUEST_FORM_NAMES.residence_municipality]: GUEST_FORM_NAMES.residence_city,
  [GUEST_FORM_NAMES.document_expedition_municipality]:
    GUEST_FORM_NAMES.document_expedition_city,
};

type BuildGuestFormDataFromDBArgs = {
  guestData: Partial<Guest>;
};
const buildGuestFormDataFromDB = ({guestData}: BuildGuestFormDataFromDBArgs) => {
  if (!guestData) return;
  const guestValues = Object.entries(guestData);

  const preloadedData: {[key: string]: string | SelectOption | PhoneDetails | Date} = {};
  guestValues.forEach(([fieldName, fieldValue]: [string, unknown]) => {
    const isFieldExist = Object.values(GUEST_FORM_NAMES).includes(
      fieldName as GUEST_FORM_NAMES,
    );

    if (!isFieldExist || !fieldValue) return;

    const isDateValue = isDateField(fieldName);
    const isLocationValue = checkIsLocationValue(fieldValue);
    const isAgreementClauseValue = isAgreementClauseField(fieldName);
    if (isAgreementClauseValue) {
      const agreementClausesFormData = buildAgreementClausesFormData(
        fieldValue as Record<string, boolean>,
      );
      preloadedData[GUEST_FORM_NAMES.agreement_clauses] = agreementClausesFormData;
      return;
    }

    if (isPhoneField(fieldName)) {
      if (getIsEmptyPhoneValue(fieldValue)) return;
      preloadedData[fieldName] = fieldValue;
      return;
    }

    if (isLocationValue && CitiesDTO[fieldName]) {
      const cityFieldName = CitiesDTO[fieldName];
      preloadedData[cityFieldName] = {
        label: fieldValue.name,
        value: fieldValue.code,
      };
      return;
    }

    if (isLocationValue) {
      preloadedData[fieldName] = {
        label: fieldValue.name,
        value: fieldValue.code,
      };
      return;
    }

    if (isDateValue) {
      preloadedData[fieldName] = dayjs(fieldValue as string).toDate();
      return;
    }

    if (isSelectField(fieldName)) {
      if (!isSelectOptionTypeValid(fieldValue)) {
        return;
      }
    }

    if (!preloadedData[fieldName]) {
      preloadedData[fieldName] = fieldValue;
    }
  });

  return preloadedData;
};

export {buildGuestFormDataFromDB};
